import React from 'react';
import { Box, Typography } from '@lucid/lego';
import type { OrderDetailsResponse } from '../../../../../../api/services/lupp/order/dto.order';
import { useStore } from '../../../../../../store/store';
import { formatCurrency } from '../../../../../../utils/tools';
import styles from './InvoiceSummary.module.scss';

type InvoiceSummaryProps = {
  invoiceSummaryDetails?: OrderDetailsResponse['invoiceSummaryDetails'];
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  dataTestId?: string;
};

/**
 * InvoiceSummary component displays the invoice breakdown
 * including pricing, taxes, payments, and amounts due.
 *
 * @component
 * @param {InvoiceSummaryProps} props - Component props
 * @returns {JSX.Element} Invoice summary section
 *
 * @example
 * ```tsx
 * <InvoiceSummary
 *   invoiceSummaryDetails={orderData.invoiceSummaryDetails}
 *   theme="dark"
 *   labels={labels}
 * />
 * ```
 */
const InvoiceSummary: React.FC<InvoiceSummaryProps> = ({
  invoiceSummaryDetails,
  theme,
  labels,
  dataTestId = 'invoice-summary',
}) => {
  const locale = useStore((state) => state.locale);

  return (
    <Box data-testid={dataTestId} className={styles[theme]}>
      <Box mb={24}>
        <Typography variant="body" size="large" theme={theme} color="black">
          {labels?.['invoice.summary'] ?? ''}
        </Typography>
      </Box>
      <Box className={styles.priceContainer}>
        <Box className={styles.priceRow}>
          <Typography variant="h5" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.totalConfigPriceExclTax'] ?? ''}
          </Typography>
          <Typography variant="body" size="small" theme={theme} color="black">
            {formatCurrency(invoiceSummaryDetails?.totalConfigPriceExclTax, locale)}
          </Typography>
        </Box>
        <Box className={styles.priceRow}>
          <Typography variant="body" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.totalCreditsDiscounts'] ?? ''}
          </Typography>
          <Typography variant="body" size="small" theme={theme} color="black">
            {formatCurrency(invoiceSummaryDetails?.totalCredits, locale)}
          </Typography>
        </Box>
        <hr className={styles.divider} />
        <Box className={styles.priceRow}>
          <Typography variant="h5" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.subtotalOrderExclTax'] ?? ''}
          </Typography>
          <Typography variant="body" size="small" theme={theme} color="black">
            {formatCurrency(invoiceSummaryDetails?.orderSubtotalExclTax, locale)}
          </Typography>
        </Box>
        <Box className={styles.priceRow}>
          <Typography variant="body" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.totalTax'] ?? ''} {invoiceSummaryDetails?.taxPercentage}%
          </Typography>
          <Typography variant="body" size="small" theme={theme} color="black">
            {formatCurrency(invoiceSummaryDetails?.salesTax, locale)}
          </Typography>
        </Box>
        <hr className={styles.divider} />
        <Box className={styles.priceRow}>
          <Typography variant="h5" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.totalOrderInclTax'] ?? ''}
          </Typography>
          <Typography variant="h5" size="small" theme={theme} color="black">
            {formatCurrency(invoiceSummaryDetails?.totalOrderInclTax, locale)}
          </Typography>
        </Box>
        <Box className={styles.priceRow}>
          <Typography variant="body" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.totalPayments'] ?? ''}
          </Typography>
          <Typography variant="body" size="small" theme={theme} color="black">
            {formatCurrency(invoiceSummaryDetails?.totalPayments, locale)}
          </Typography>
        </Box>
        <Box className={styles.priceRow}>
          <Typography variant="h5" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.totalAmountDue'] ?? ''}
          </Typography>
          <Typography variant="h5" size="small" theme={theme} color="black">
            {formatCurrency(invoiceSummaryDetails?.totalAmountDue, locale)}
          </Typography>
        </Box>
        <Box className={styles.highlighted}>
          <Box className={styles.priceRow}>
            <Typography variant="body" size="small" theme={theme} color="black">
              {labels?.['invoice.summary.amountDuePriorToHandoff'] ?? ''}
            </Typography>
            <Typography variant="body" size="small" theme={theme} color="black">
              {formatCurrency(invoiceSummaryDetails?.amountDuePriorToHandoff, locale)}
            </Typography>
          </Box>
        </Box>
        <Box className={styles.priceRow}>
          <Typography variant="body" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.totalRefunds'] ?? ''}
          </Typography>
          <Typography variant="body" size="small" theme={theme} color="black">
            {formatCurrency(invoiceSummaryDetails?.totalRefunds, locale)}
          </Typography>
        </Box>
        <Box className={styles.priceRow}>
          <Typography variant="body" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.netPayments'] ?? ''}
          </Typography>
          <Typography variant="body" size="small" theme={theme} color="black">
            {formatCurrency(invoiceSummaryDetails?.netPayments, locale)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

InvoiceSummary.displayName = 'InvoiceSummary';

export default InvoiceSummary;
